import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CFormCheck,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  createNewBooking,
  getSingleBookingOrder,
  updateBookingOrderData,
} from "../../services/booking.services";
import { getSearchableStoreList } from "../../services/store.services";
import { getSearchedPlasticSealList } from "src/services/plastic-seal.services";
import {
  getActiveCountryList,
  getCountryList,
} from "src/services/country.services";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import { useQuery } from "@tanstack/react-query";
import { getAllUnitService } from "src/services/unit.services";
import { getAllTypeService } from "src/services/type.services";
import { useLocation, useNavigate, useParams } from "react-router";
import { isDisableInput, isDisableSelect } from "src/uitils/helper";

const schema = z.object({
  bookingOrderId: z.string().min(1, "Required"),
  fromLocation: z.string().min(1, "Required"),
  toLocation: z.string().min(1, "Required"),
  country: z.string(),
  transferOrderNo: z.string().min(1, "Required"),
  sealBag: z.string().min(1, "Required"),
  priority: z.string().min(1, "Required"),
  pickupDate: z.string().min(1, "Required"),
  pickupTime: z.string().min(1, "Required"),
  remarks: z.string(),
  route: z.string().optional(),
  type: z.string().min(1, { message: "Type is required" }),
  unit: z.string().min(1, { message: "Unit is required" }),
  status: z.string().nullable().optional(),
  isTransffered: z.string().optional(),
});

const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "cvit", label: "CDT" },
  { value: "new", label: "New" },
  { value: "assigned", label: "Assigned" },
  { value: "driver", label: "Accepted" },
  { value: "delivered", label: "Delivered" },
  { value: "rejected", label: "Rejected" },
];

const NewBookingOrderForm = () => {
  const navigate = useNavigate();
  const userInfo = JSON.parse(localStorage.getItem("user"));

  //get orderid from params
  const { bookingOrderId } = useParams();
  const { pathname } = useLocation();

  const [fixedFromLocation, setFixedFromLocation] = useState(false);
  const [transferOrderValue, setTransferOrderValue] = useState("");
  const [selectedFromStoreOption, setSelectedFromStoreOption] = useState(null);
  const [selectedToStoreOption, setSelectedToStoreOption] = useState(null);
  const [locationCodeOptions, setLocationCodeOptions] = useState([]);
  const [selectedSealOption, setSelectedSealOption] = useState(null);
  const [storeDataArray, setStoreDataArray] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [activeCountries, setActiveCountries] = useState([]);
  const [countrySelectOptions, setCountrySelectOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPriorityOption, setSelectedPriorityOption] = useState({
    label: "normal",
    value: "normal",
  });
  const [singleOrderData, setSingleOrderData] = useState(null);
  const [priorityOptions, setPriorityOptions] = useState([
    { label: "normal", value: "normal" },
    { label: "emergency", value: "emergency" },
  ]);
  const [sealOptions, setSealOptions] = useState([]);
  const [sealArray, setSealArray] = useState([]);
  const [selectedFromLocationCode, setSelectedFromLocationCode] =
    useState(null);
  const [selectedToLocationCode, setSelectedToLocationCode] = useState(null);
  const [selectedToRoute, setSelectedToRoute] = useState(null);
  const [statusOptionList, setStatusOptionList] = useState(statusOptions);
  const [selectedStatusOption, setSelectedStatusOption] = useState(null);
  const [isTransffered, setIsTransferred] = useState("");
  // new Date().toISOString().split("T")[0]
  const currentDate = moment(new Date()).format("DD/MM/YYYY");

  const [pickupDate, setPickupDate] = useState(currentDate);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [unitOptions, setUnitOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedTypeOption, setSelectedTypeOption] = useState("");
  const [selectedUnitOption, setSelectedUnitOption] = useState("");

  const {
    data: unitAndTypeQueryData,
    isLoading,
    status,
  } = useQuery({
    queryKey: ["unitsAndType", "getUnitsAndType"],
    queryFn: () => Promise.all([getAllUnitService(), getAllTypeService()]),
    enabled: true,
  });

  const {
    data: singleBookingOrderQueryData,
    isLoading: isLoadingSingleBookingOrderQuery,
    status: statusSingleBookingOrderQuery,
  } = useQuery({
    queryKey: ["singleBookingOrder"],
    queryFn: () => getSingleBookingOrder(bookingOrderId),
    enabled: !!bookingOrderId,
  });

  useEffect(() => {
    if (status === "success" && !isLoading && unitAndTypeQueryData) {
      const unitQueryData = unitAndTypeQueryData[0];
      const TypeQueryData = unitAndTypeQueryData[1];
      if (
        unitQueryData.data &&
        Array.isArray(unitQueryData.data) &&
        unitQueryData.data.length > 0
      ) {
        const unitOptions = unitQueryData.data.map((unit) => ({
          value: unit._id,
          label: unit.name,
        }));
        setUnitOptions(unitOptions);
        const findOption = unitOptions.find(
          (unit) => unit.label.toLowerCase() === "packet"
        );
        if (findOption) {
          setSelectedUnitOption(findOption);
          setValue("unit", findOption.value);
        }
      }

      if (
        TypeQueryData.data &&
        Array.isArray(TypeQueryData.data) &&
        TypeQueryData.data.length > 0
      ) {
        const unitOptions = TypeQueryData.data.map((unit) => {
          if (unit.name.toLowerCase() === "jewellery") {
            setSelectedTypeOption({
              value: unit._id,
              label: unit.name,
            });
            setValue("type", unit._id);
          }
          return {
            value: unit._id,
            label: unit.name,
          };
        });
        setTypeOptions(unitOptions);
      }
    }
  }, [unitAndTypeQueryData, isLoading, status]);

  useEffect(() => {
    // getActiveCountries();
    if (userInfo && userInfo.country) {
      setSelectedCountry({
        value: userInfo.country._id,
        label: `${userInfo.country.name}-${userInfo.country.code}`,
      });
      setValue("country", userInfo.country._id);

      getStoreInfo(userInfo.country._id);
      getSealInfo(userInfo.country._id);
    }

    if (userInfo && userInfo.userType === "superAdmin") {
      getActiveCountries();
    }
  }, []);

  useEffect(() => {
    if (
      !["admin", "superAdmin"].includes(userInfo.userType) &&
      userInfo.location
    ) {
      setFixedFromLocation(true);
      setSelectedFromStoreOption({
        label: userInfo.location.name,
        value: userInfo.location._id,
      });
      setValue("fromLocation", userInfo.location._id);
      setSelectedFromLocationCode({
        value: userInfo.location?._id,
        label: userInfo?.location?.locationCode
          ? userInfo.location.locationCode
          : "N/A",
      });
    }
  }, []);

  useEffect(() => {
    if (storeDataArray.length > 0) {
      if (userInfo.userType === "store" && userInfo.location._id) {
        const storeObj = storeDataArray.find(
          (x) => x._id === userInfo?.location?._id
        );
        setRouteBasedOnUserType("from", storeObj);
      }
    }
  }, [storeDataArray]);

  useEffect(() => {
    setValue("pickupDate", moment(new Date()).format("YYYY-MM-DD"));
  }, []);

  const getActiveCountries = () => {
    getActiveCountryList({ limit: 100, page: 1 })
      .then((response) => {
        if (response.success && response.data) {
          let options = response.data.map((element) => ({
            value: `${element._id}`,
            label: `${element.name}`,
          }));
          setCountrySelectOptions(options);
        }
      })
      .catch((error) => console.log(error));
  };

  const getSealInfo = (country, searchedString = "") => {
    const limit = 10;
    const page = 1;
    getSearchedPlasticSealList({ limit, page, searchedString, country })
      .then(async (response) => {
        const storeData = response.data;
        let options = [];
        let serialArray = [];

        storeData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${element.serialNo}`,
          };
          options.push(option);
          serialArray.push(element.serialNo);
        });
        setSealArray(serialArray);
        setSealOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getStoreInfo = (country = "") => {
    getSearchableStoreList({ country })
      .then(async (response) => {
        setStoreDataArray(response.data);
        const storeData = response.data;
        let options = [];
        let locationCodeOptions = [];

        // storeData.filter((item)=>if(userInfo.location && userInfo.location._id ))
        storeData.forEach((element) => {
          // const option = { value: `${element._id}`, label: `${element.name}` };
          // const codeOption = {
          //   value: `${element._id}`,
          //   label: element.locationCode ? element.locationCode : "N/A",
          // };
          // options.push(option);
          // locationCodeOptions.push(codeOption);
          //  // Check user type and location existence first:

          // Create options with conditional filtering:
          const option = {
            value: `${element._id}`,
            label: `${element.name}`,
          };
          const codeOption = {
            value: `${element._id}`,
            label: element.locationCode ? element.locationCode : "N/A",
          };

          if (
            userInfo &&
            userInfo.location &&
            userInfo.location._id !== element._id
          ) {
            options.push(option);
            locationCodeOptions.push(codeOption);
          } else {
            options.push(option);
            locationCodeOptions.push(codeOption);
          }
        });
        setStoreOptions(options);
        setLocationCodeOptions(locationCodeOptions);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      bookingOrderId: "OI" + new Date().getTime(),
      country: "",
      fromLocation: "",
      toLocation: "",
      transferOrderNo: "",
      sealBag: "",
      type: "",
      unit: "",
      priority: "normal",
      pickupDate: "",
      pickupTime: moment().format("HH:mm") || "",
      remarks: "",
      route: "",
    },
    resolver: zodResolver(schema),
  });

  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    "Something went wrong. Please try again"
  );
  const onSubmit = (data) => {
    setIsSubmitted(true);
    if (bookingOrderId) {
      toast
        .promise(updateBookingOrderData(data, bookingOrderId), {
          loading: "Updating....",
          success: "Booking order successfully updated",
          error: (err) => {
            return err.response &&
              err.response.data &&
              err.response.data.message
              ? err.response.data.message
              : defaultErrorMessage;
          },
        })
        .then(async (response) => {
          console.log("res", response);
          if (response.success) {
            navigate("/manage-booking-orders", { replace: true });
          }
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setIsSubmitted(false);
        });
    } else {
      toast
        .promise(
          createNewBooking(data),
          {
            loading: "Loading",
            success: "Booking Order Created Succesfully",
            error: (err) => {
              return err.response &&
                err.response.data &&
                err.response.data.message
                ? err.response.data.message
                : defaultErrorMessage;
            },
          },
          {
            id: "booking-order",
          }
        )
        .then(async (response) => {
          reset();
          if (userInfo?.country) {
            setValue("country", userInfo.country._id);
          }
          handlePickupDateChange(new Date());
          refreshCustomField();
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setIsSubmitted(false);
        });
    }
  };

  const onError = (err) => {
    console.log(typeof getValues("isTransffered"), getValues("isTransffered"));
    setIsSubmitted(false);
    console.error(err);
  };

  const refreshCustomField = () => {
    if (userInfo && userInfo.userType === "superAdmin") {
      setSelectedCountry(null);
      setValue("country", "");
    }

    setSelectedToStoreOption(null);
    setSelectedSealOption(null);
    setValue("bookingOrderId", "OI" + new Date().getTime());
    if (selectedCountry) {
      getSealInfo(selectedCountry.value);
    }
    setTransferOrderValue("");
    setSelectedToLocationCode("");
    setSelectedPriorityOption({ label: "normal", value: "normal" });
    setValue("priority", "normal");

    if (
      typeOptions &&
      Array.isArray(typeOptions) &&
      typeOptions.length > 0 &&
      !bookingOrderId
    ) {
      typeOptions.map((unit) => {
        if (unit.label.toLowerCase() === "jewellery") {
          setSelectedTypeOption(unit);
          setValue("type", unit.value);
        }
      });
    }

    if (unitOptions && Array.isArray(unitOptions) && unitOptions.length > 0) {
      const findOption = unitOptions.find(
        (unit) => unit.label.toLowerCase() === "packet"
      );
      if (findOption) {
        setSelectedUnitOption(findOption);
        setValue("unit", findOption.value);
      }
    }

    if (
      fixedFromLocation &&
      (userInfo.userType === "store" || userInfo.userType === "others")
    ) {
      setSelectedFromStoreOption({
        label: userInfo.location.name,
        value: userInfo.location._id,
      });
      setValue("fromLocation", userInfo.location._id);

      setSelectedFromLocationCode({
        value: userInfo.location?._id,
        label: userInfo?.location?.locationCode
          ? userInfo.location.locationCode
          : "N/A",
      });

      const storeObj = storeDataArray.find(
        (x) => x._id === userInfo.location._id
      );
      setRouteBasedOnUserType("from", storeObj);
    } else {
      setSelectedFromStoreOption(null);
      setSelectedFromLocationCode("");
      setSelectedToRoute(null);
      setValue("route", "");
    }

    // if (fixedFromLocation) {
    //   setSelectedFromStoreOption({
    //     label: userInfo.location.name,
    //     value: userInfo.location._id,
    //   });
    //   setValue("fromLocation", userInfo.location._id);
    //   setSelectedFromLocationCode(userInfo.location.locationCode);
    // }
  };

  const handleCountryChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption);
      setSelectedFromLocationCode("");
      setSelectedFromStoreOption(null);
      setSelectedToLocationCode("");
      setSelectedToStoreOption(null);
      setValue("country", selectedOption.value);
      getStoreInfo(selectedOption?.value);
      getSealInfo(selectedOption.value);
    } else {
      setSelectedCountry(null);
      setValue("country", "");
      setSelectedFromLocationCode("");
      setSelectedFromStoreOption(null);
      setSelectedToLocationCode("");
      setSelectedToStoreOption(null);
      setSealOptions([]);
    }
  };

  const handleOnInputChangeSeal = (selectedOption) => {
    if (selectedOption) {
      const expectedData = sealArray.find((a) => a.includes(selectedOption));
      if (!expectedData) {
        getSealInfo(selectedCountry.value, selectedOption);
      }
    } else {
      getSealInfo(selectedCountry.value);
    }
  };

  const handleChangeSeal = (selectedOption) => {
    if (selectedOption) {
      setSelectedSealOption(selectedOption);
      setValue("sealBag", selectedOption.value);
      clearErrors("sealBag");
    } else {
      setSelectedSealOption("");
      setValue("sealBag", "");
    }
  };

  const handleChangeFromLocationCode = (selectedOption) => {
    if (selectedOption) {
      setSelectedFromLocationCode(selectedOption);
      setValue("fromLocation", selectedOption.value);
      const storeObj = storeDataArray.find(
        (x) => x._id === selectedOption.value
      );
      setRouteBasedOnUserType("from", storeObj);
      setSelectedFromStoreOption({ value: storeObj._id, label: storeObj.name });
      clearErrors("fromLocation");
    } else {
      setSelectedFromStoreOption("");
      setValue("fromLocation", "");
      setSelectedFromLocationCode("");
      clearRouteBasedOnUserType("from");
    }
  };

  const handleChangeFromStore = (selectedOption) => {
    if (selectedOption) {
      setSelectedFromStoreOption(selectedOption);
      setValue("fromLocation", selectedOption.value);
      const storeObj = storeDataArray.find(
        (x) => x._id === selectedOption.value
      );
      setSelectedFromLocationCode({
        value: storeObj._id,
        label: storeObj.locationCode ? storeObj.locationCode : "N/A",
      });
      setRouteBasedOnUserType("from", storeObj);
      clearErrors("fromLocation");
    } else {
      setSelectedFromStoreOption("");
      setValue("fromLocation", "");
      setSelectedFromLocationCode("");
      clearRouteBasedOnUserType("from");
    }
  };

  const handleChangeToLocationCode = (selectedOption) => {
    if (selectedOption) {
      setSelectedToLocationCode(selectedOption);
      setValue("toLocation", selectedOption.value);
      const storeObj = storeDataArray.find(
        (x) => x._id === selectedOption.value
      );
      setRouteBasedOnUserType("to", storeObj);
      setSelectedToStoreOption({ value: storeObj._id, label: storeObj.name });
      clearErrors("toLocation");
    } else {
      setSelectedToStoreOption("");
      setValue("toLocation", "");
      setSelectedToLocationCode("");
      clearRouteBasedOnUserType("to");
    }
  };

  const handleChangeToStore = (selectedOption) => {
    if (selectedOption) {
      setSelectedToStoreOption(selectedOption);
      setValue("toLocation", selectedOption.value);

      const storeObj = storeDataArray.find(
        (x) => x._id === selectedOption.value
      );
      setSelectedToLocationCode({
        value: storeObj._id,
        label: storeObj.locationCode ? storeObj.locationCode : "N/A",
      });

      setRouteBasedOnUserType("to", storeObj);
      clearErrors("toLocation");
    } else {
      setSelectedToStoreOption("");
      setValue("toLocation", "");
      setSelectedToLocationCode("");
      clearRouteBasedOnUserType("to");
    }
  };

  const handleChangeType = (selectedOption) => {
    if (selectedOption) {
      setSelectedTypeOption(selectedOption);
      setValue("type", selectedOption.value);
    } else {
      setSelectedTypeOption("");
      setValue("type", "");
    }
  };

  const handleChangeUnit = (selectedOption) => {
    if (selectedOption) {
      setSelectedUnitOption(selectedOption);
      setValue("unit", selectedOption.value);
    } else {
      setSelectedUnitOption("");
      setValue("unit", "");
    }
  };

  const handleChangePriority = (selectedOption) => {
    if (selectedOption) {
      setSelectedPriorityOption(selectedOption);
      setValue("priority", selectedOption.value);
    } else {
      setSelectedPriorityOption("");
      setValue("priority", "");
    }
  };

  const handleTransferOrderChange = (e) => {
    const value = e.target.value;

    // Use a regular expression to match alphanumeric characters, plus (+), and minus (-).
    // You can customize the regex pattern as needed.

    const regexPattern = /^[a-zA-Z0-9-]*$/;

    if (regexPattern.test(value)) {
      setValue("transferOrderNo", value);
      setTransferOrderValue(value);
      clearErrors("transferOrderNo");
    }
  };

  const handlePickupDateChange = (date) => {
    let formated = moment(date).format("DD/MM/YYYY");
    setPickupDate(formated);
    setValue("pickupDate", moment(date).format("YYYY-MM-DD"));
  };

  // const set route based on user type
  const setRouteBasedOnUserType = (actionType, storeObj) => {
    if (userInfo?.userType === "store" && actionType === "from") {
      setSelectedToRoute(storeObj?.route ? storeObj.route : null);
      setValue("route", storeObj?.route?._id ? storeObj.route._id : "");
    }

    if (
      ["admin", "cvit", "others", "superAdmin"].includes(userInfo?.userType) &&
      actionType === "to"
    ) {
      setSelectedToRoute((prev) => (storeObj?.route ? storeObj.route : null));
      setValue("route", storeObj?.route?._id ? storeObj.route._id : "");
    }
  };

  // clear route based on user type
  const clearRouteBasedOnUserType = (actionTYpe) => {
    if (userInfo?.userType === "store" && actionTYpe === "from") {
      setSelectedToRoute(null);
      setValue("route", "");
    }

    if (
      ["admin", "cvit", "others", "superAdmin"].includes(userInfo?.userType) &&
      actionTYpe === "to"
    ) {
      setSelectedToRoute(null);
      setValue("route", "");
    }
  };

  const handleChangeStatus = (selectedOption) => {
    if (selectedOption) {
      const orderData = singleBookingOrderQueryData.data;
      if (
        orderData?.userType.type !== "store" &&
        selectedOption.value === "new"
      ) {
        toast.error(
          "Orders not created by store user, so you can't select new status"
        );
      } else {
        setSelectedStatusOption(selectedOption);
        setValue("status", selectedOption.value);
      }
    } else {
      setSelectedStatusOption("");
      setValue("status", "");
    }
  };

  useEffect(() => {
    if (bookingOrderId && isLoadingSingleBookingOrderQuery) {
      toast.loading("Loading.....");
    }

    if (
      !isLoadingSingleBookingOrderQuery &&
      singleBookingOrderQueryData &&
      statusSingleBookingOrderQuery === "success"
    ) {
      toast.dismiss();
      const orderData = singleBookingOrderQueryData.data;
      setSingleOrderData(orderData);

      if (orderData && typeof orderData === "object") {
        // console.log(orderData);
        setValue("bookingOrderId", orderData.bookingOrderId);
        if (userInfo?.userType?.type === "superAdmin") {
          if (countrySelectOptions && countrySelectOptions.length > 0) {
            setValue("country", orderData.country._id);
            const findCountry = countrySelectOptions.find(
              (item) => item.value === orderData.country
            );
            setSelectedCountry(findCountry);
          }
        } else {
          setValue("country", orderData.country);
        }

        if (
          orderData.fromLocation &&
          typeof orderData.fromLocation === "object"
        ) {
          setSelectedFromLocationCode({
            value: orderData.fromLocation._id,
            label: orderData.fromLocation.locationCode
              ? orderData.fromLocation.locationCode
              : "N/A",
          });
          setSelectedFromStoreOption({
            value: orderData.fromLocation._id,
            label: orderData.fromLocation.name
              ? orderData.fromLocation.name
              : "N/A",
          });

          setValue("fromLocation", orderData.fromLocation._id);
        }

        if (orderData.toLocation && typeof orderData.toLocation === "object") {
          setSelectedToLocationCode({
            value: orderData.toLocation._id,
            label: orderData.toLocation.locationCode
              ? orderData.toLocation.locationCode
              : "N/A",
          });
          setSelectedToStoreOption({
            value: orderData.toLocation._id,
            label: orderData.toLocation.name
              ? orderData.toLocation.name
              : "N/A",
          });
          setValue("toLocation", orderData.toLocation._id);
        }

        if (orderData.tripRoute && typeof orderData.tripRoute === "object") {
          setSelectedToRoute(orderData.tripRoute);
          setValue("route", orderData.tripRoute._id);
        }

        if (orderData.type && typeof orderData.type === "object") {
          setSelectedTypeOption({
            value: orderData.type._id,
            label: orderData.type.name,
          });
          setValue("type", orderData.type._id);
        }
        if (orderData.unit && typeof orderData.unit === "object") {
          setSelectedUnitOption({
            value: orderData.unit._id,
            label: orderData.unit.name,
          });
          setValue("unit", orderData.unit._id);
        }
        if (orderData.priority === "normal") {
          setSelectedPriorityOption({
            value: "normal",
            label: "Normal",
          });
          setValue("priority", orderData.priority);
        }

        if (orderData.priority === "emergency") {
          setSelectedPriorityOption({
            value: "emergency",
            label: "Emergency",
          });
          setValue("priority", orderData.priority);
        }
        if (orderData.transferOrderNo) {
          setTransferOrderValue(orderData.transferOrderNo);
          setValue("transferOrderNo", orderData.transferOrderNo);
        }

        if (orderData.sealBag && typeof orderData.sealBag === "object") {
          setValue("sealBag", orderData.sealBag._id);
          setSelectedSealOption({
            value: orderData.sealBag._id,
            label: orderData.sealBag.serialNo,
          });
        }

        if (orderData.pickupDate) {
          setValue("pickupDate", orderData.pickupDate);
          setPickupDate(orderData.pickupDate);
        }

        if (orderData.pickupTime) {
          setValue("pickupTime", orderData.pickupTime);
        }

        if (orderData.userType.type === "store") {
          if (orderData.status === "pending") {
            setStatusOptionList(
              statusOptions.filter((item) => item.value === "pending")
            );
          } else if (orderData.status === "cvit") {
            setStatusOptionList(
              statusOptions.filter((item) => item.value === "new")
            );
          } else if (orderData.status === "assigned") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["new", "assigned"].includes(item.value)
              )
            );
          } else if (orderData.status === "driver") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["new", "assigned", "driver"].includes(item.value)
              )
            );
          } else if (orderData.status === "delivered") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["new", "assigned", "driver", "delivered"].includes(item.value)
              )
            );
          } else if (orderData.status === "rejected") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "rejected"].includes(item.value)
              )
            );
          }
        } else {
          if (orderData.status === "pending") {
            setStatusOptionList(
              statusOptions.filter((item) => item.value === "pending")
            );
          } else if (orderData.status === "cvit") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "cvit"].includes(item.value)
              )
            );
          } else if (orderData.status === "assigned") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "cvit", "assigned"].includes(item.value)
              )
            );
          } else if (orderData.status === "driver") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "cvit", "assigned", "driver"].includes(item.value)
              )
            );
          } else if (orderData.status === "delivered") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "cvit", "assigned", "driver", "delivered"].includes(
                  item.value
                )
              )
            );
          } else if (orderData.status === "rejected") {
            setStatusOptionList(
              statusOptions.filter((item) =>
                ["pending", "rejected"].includes(item.value)
              )
            );
          }
        }

        if (orderData.status) {
          const findStatus = statusOptions.find(
            (item) => item.value === orderData.status
          );

          if (
            findStatus &&
            findStatus.value === "cvit" &&
            orderData?.userType?.type === "store"
          ) {
            setSelectedStatusOption({
              value: "new",
              label: "New",
            });
            setValue("status", "new");
            setValue(
              "isTransffered",
              orderData.isTransffered ? "true" : "false"
            );
            setIsTransferred(orderData.isTransffered ? "true" : "false");
          } else {
            setSelectedStatusOption(findStatus);
            setValue("status", orderData.status);
          }
        }
        setValue("remarks", orderData.remarks ? orderData.remarks : "");
      }
    }
  }, [
    bookingOrderId,
    singleBookingOrderQueryData,
    isLoadingSingleBookingOrderQuery,
    statusSingleBookingOrderQuery,
  ]);

  useEffect(() => {
    if (singleOrderData && countrySelectOptions.length > 0) {
      const findCountry = countrySelectOptions.find(
        (item) => item.value === singleOrderData.country
      );
      setSelectedCountry(findCountry);
    }
  }, [singleOrderData, countrySelectOptions]);

  useEffect(() => {
    if (singleOrderData && !bookingOrderId) {
      refreshCustomField();
      setSingleOrderData(null);
    }
  }, [pathname, bookingOrderId, singleOrderData]);
  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              {bookingOrderId
                ? "Update Booking Order"
                : "Create New Booking Order"}
            </h4>
            {/* <div className="small text-medium-emphasis">January - July 2021</div> */}
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="bookingOrderId"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Booking Order ID:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <CFormInput
                    type="text"
                    id="bookingOrderId"
                    {...register("bookingOrderId")}
                    disabled={true}
                  />
                  {errors.bookingOrderId?.message && (
                    <p className="error_text">
                      {errors.bookingOrderId?.message}
                    </p>
                  )}
                </CCol>
              </CRow>
              {userInfo?.userType === "superAdmin" && (
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="firstName"
                    className="col-sm-3 col-form-label"
                    style={{ width: "40%" }}
                  >
                    Country:
                  </CFormLabel>
                  <CCol sm={8} style={{ width: "60%" }}>
                    <Select
                      value={selectedCountry}
                      options={countrySelectOptions}
                      onChange={handleCountryChange}
                      isClearable={true}
                      placeholder="Select Country"
                      isDisabled={isDisableInput(
                        "country",
                        userInfo,
                        singleOrderData
                      )}
                      classNames={isDisableSelect}
                    />
                    {errors.country?.message && (
                      <p className="error_text">{errors.country?.message}</p>
                    )}
                  </CCol>
                </CRow>
              )}

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="fromLocation"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  From Location Code:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedFromLocationCode}
                    onChange={handleChangeFromLocationCode}
                    options={locationCodeOptions}
                    isClearable={true}
                    isDisabled={isDisableInput(
                      "fromLocation",
                      userInfo,
                      singleOrderData,
                      fixedFromLocation,
                      selectedCountry
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.fromLocation?.message && (
                    <p className="error_text">{errors.fromLocation?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="fromLocation"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  From Location Name:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedFromStoreOption}
                    onChange={handleChangeFromStore}
                    options={storeOptions}
                    isClearable={true}
                    isDisabled={isDisableInput(
                      "fromLocation",
                      userInfo,
                      singleOrderData,
                      fixedFromLocation,
                      selectedCountry
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.fromLocation?.message && (
                    <p className="error_text">{errors.fromLocation?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="fromLocation"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  To Location Code:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedToLocationCode}
                    onChange={handleChangeToLocationCode}
                    options={locationCodeOptions}
                    isClearable={true}
                    isDisabled={isDisableInput(
                      "toLocation",
                      userInfo,
                      singleOrderData,
                      selectedCountry
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.toLocation?.message && (
                    <p className="error_text">{errors.toLocation?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="orderNumber"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  To Location Name:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedToStoreOption}
                    onChange={handleChangeToStore}
                    options={storeOptions}
                    isClearable={true}
                    isDisabled={isDisableInput(
                      "toLocation",
                      userInfo,
                      singleOrderData,
                      selectedCountry
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.toLocation?.message && (
                    <p className="error_text">{errors.toLocation?.message}</p>
                  )}
                </CCol>
              </CRow>
              {["admin", "cvit", "store", "superAdmin"].includes(
                userInfo.userType
              ) && (
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="fromLocationCode"
                    className="col-sm-3 col-form-label"
                    style={{ width: "40%" }}
                  >
                    Route Name:
                  </CFormLabel>
                  <CCol sm={8} style={{ width: "60%" }}>
                    <CFormInput
                      type="text"
                      id="fromLocationCode"
                      value={
                        selectedToRoute?.name
                          ? selectedToRoute.name
                          : selectedToLocationCode
                          ? "N/A"
                          : ""
                      }
                      disabled
                    />
                  </CCol>
                </CRow>
              )}
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="orderNumber"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Select Type:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedTypeOption}
                    onChange={handleChangeType}
                    options={typeOptions}
                    isClearable={true}
                    className="text-capitalize"
                    placeholder="select type"
                    isDisabled={isDisableInput(
                      "TYPE",
                      userInfo,
                      singleOrderData
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.type?.message && (
                    <p className="error_text">{errors.type?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="orderNumber"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Select Unit:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedUnitOption}
                    onChange={handleChangeUnit}
                    options={unitOptions}
                    isClearable={true}
                    className="text-capitalize"
                    placeholder="Select unit"
                    isDisabled={isDisableInput(
                      "UNIT",
                      userInfo,
                      singleOrderData
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.unit?.message && (
                    <p className="error_text">{errors.unit?.message}</p>
                  )}
                </CCol>
              </CRow>
            </CCol>

            <CCol sm={6} className="d-none d-md-block">
              {userInfo?.permission?.can_update_booking_order_status &&
                bookingOrderId && (
                  <>
                    <CRow className="mb-3">
                      <CFormLabel
                        htmlFor="orderNumber"
                        className="col-sm-3 col-form-label"
                        style={{ width: "40%" }}
                      >
                        Status:
                      </CFormLabel>
                      <CCol sm={8} style={{ width: "60%" }}>
                        <Select
                          value={selectedStatusOption}
                          onChange={handleChangeStatus}
                          options={statusOptionList}
                          isClearable={true}
                          className="text-capitalize"
                          classNames={isDisableSelect}
                        />
                      </CCol>
                    </CRow>

                    {selectedStatusOption &&
                      selectedStatusOption.value === "new" && (
                        <CRow className="mb-3">
                          <CFormLabel
                            className="col-sm-3 col-form-label"
                            style={{ width: "40%" }}
                          >
                            Order Belongs to:
                          </CFormLabel>
                          <CCol sm={8} style={{ width: "60%" }}>
                            <CFormCheck
                              inline
                              type="radio"
                              name="orderBelongsTo"
                              id="inlineCheckbox1"
                              value="false"
                              checked={getValues("isTransffered") === "false"}
                              label="Store"
                              onChange={() => {
                                setValue("isTransffered", "false");
                                setIsTransferred("false");
                              }}
                            />
                            <CFormCheck
                              inline
                              type="radio"
                              name="orderBelongsTo"
                              id="inlineCheckbox2"
                              value="true"
                              checked={getValues("isTransffered") === "true"}
                              label="CDT"
                              onChange={() => {
                                setValue("isTransffered", "true");
                                setIsTransferred("true");
                              }}
                            />
                          </CCol>
                        </CRow>
                      )}
                  </>
                )}
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="orderNumber"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Priority:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedPriorityOption}
                    onChange={handleChangePriority}
                    options={priorityOptions}
                    isClearable={true}
                    className="text-capitalize"
                    isDisabled={isDisableInput(
                      "PRIORITY",
                      userInfo,
                      singleOrderData
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.priority?.message && (
                    <p className="error_text">{errors.priority?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="transferOrderNo"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Transfer Order No.:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <CFormInput
                    type="text"
                    id="transferOrderNo"
                    value={transferOrderValue}
                    onChange={handleTransferOrderChange}
                    autoComplete="off"
                    disabled={isDisableInput(
                      "transferOrderNo",
                      userInfo,
                      singleOrderData
                    )}
                  />
                  {errors.transferOrderNo?.message && (
                    <p className="error_text">
                      {errors.transferOrderNo?.message}
                    </p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="sealBag"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Seal/Bag No:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <Select
                    value={selectedSealOption}
                    onChange={handleChangeSeal}
                    onInputChange={handleOnInputChangeSeal}
                    options={sealOptions}
                    isClearable={true}
                    isDisabled={isDisableInput(
                      "sealBag",
                      userInfo,
                      singleOrderData,
                      selectedCountry
                    )}
                    classNames={isDisableSelect}
                  />
                  {errors.sealBag?.message && (
                    <p className="error_text">{errors.sealBag?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="pickupDate"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Pick-up Date:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <ReactDatePicker
                    showIcon={true}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    onChange={handlePickupDateChange}
                    minDate={new Date()}
                    value={pickupDate}
                    // disabled={isDisableInput(
                    //   "pickupDate",
                    //   userInfo,
                    //   singleOrderData
                    // )}
                    disabled
                  />
                  {errors.pickupDate?.message && (
                    <p className="error_text">{errors.pickupDate?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="pickupTime"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Pick-up Time:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <CFormInput
                    type="time"
                    id="pickupTime"
                    {...register("pickupTime")}
                    disabled={isDisableInput(
                      "pickupTime",
                      userInfo,
                      singleOrderData
                    )}
                  />
                  {errors.pickupTime?.message && (
                    <p className="error_text">{errors.pickupTime?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="remarks"
                  className="col-sm-3 col-form-label"
                  style={{ width: "40%" }}
                >
                  Remarks:
                </CFormLabel>
                <CCol sm={8} style={{ width: "60%" }}>
                  <CFormTextarea
                    id="exampleFormControlTextarea1"
                    style={{ height: "160px" }}
                    {...register("remarks")}
                    disabled={isDisableInput(
                      "remarks",
                      userInfo,
                      singleOrderData
                    )}
                  ></CFormTextarea>
                  {errors.remarks?.message && (
                    <p className="error_text">{errors.remarks?.message}</p>
                  )}
                </CCol>
              </CRow>
            </CCol>
          </CRow>
          <div className="d-flex justify-content-end mt-3">
            <CButton
              type="submit"
              className="px-5 py-2 btn-primary"
              disabled={isSubmitted}
            >
              {bookingOrderId ? "Update Booking Order" : "Create Booking Order"}
            </CButton>
          </div>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default NewBookingOrderForm;
